import { i18n } from 'boot/i18n';
import { filterPropertyTypes } from 'src/constants/filters';
import { PROGRAM_STATUS_META, TARGET_STATUS_META } from 'src/contexts/treatmentPlanning';

/** @todo move to `src/utils/ProgramUtils.js` */
const defaultPromptLevelColors = {
  successful: '#c1f4cd',
  default: '#ffc0c6',
  unknown: '#aeaeae',
};

/**
 * Presentation for the seven target statuses. It lives here rather than in the context because a
 * bounded context does not model icons and colours.
 */
const TARGET_STATUS_PRESENTATION = {
  waiting: { icon: 'hourglass_empty', color: 'grey-6' },
  probe: { icon: 'assignment', color: 'warning' },
  acquisition: { icon: 'school', color: 'primary' },
  mastered: { icon: 'emoji_events', color: 'positive' },
  closed: { icon: 'check', color: 'positive' },
  hold: { icon: 'pan_tool', color: 'negative' },
  aborted: { icon: 'cancel_presentation', color: 'negative' },
};

/**
 * Legacy shape for the files still reading `targetStatuses` directly. The vocabulary and its date
 * rules live in `src/contexts/treatmentPlanning`; this shim adds presentation and reshapes the rules
 * into the flags the existing readers expect.
 *
 * `label` RESOLVES ON ACCESS, and that is the point. This module is registered as a boot file purely
 * to force it to evaluate after `i18n`, because it used to call `i18n.t()` at module scope — while
 * the user's locale only loads at login, long afterwards. Every label here was therefore frozen in
 * English for every user in every locale. A getter re-reads on each access, so a component picks the
 * right one up on its next render.
 *
 * Note that `merge()` and spreading both flatten the getter to a string at the moment they run. That
 * is fine wherever it happens after login — `workflowStatuses` merges these inside a Pinia getter,
 * which re-evaluates.
 *
 * The arrow is one-way: this file imports from the context, never the reverse.
 */
const targetStatuses = Object.fromEntries(
  Object.entries(TARGET_STATUS_META).map(([key, meta]) => {
    const resets = new Set(meta.resetsDates);
    // The optional flags are spread in only when true, because the shape this replaces omitted them
    // rather than setting them false. Every reader today passes a default, so the two are equivalent
    // — but a reader doing `'hasHoldDate' in status` would not be, and a drop-in should be one.
    const whenTrue = (condition, flag) => (condition ? { [flag]: true } : {});
    const entry = {
      type: key,
      value: meta.value,
      // Optional chaining because this is a boot file: a status added in Treatment Planning without
      // a presentation entry here would otherwise throw at module scope and the app would not
      // start. Undefined is what the program-status branch below already yields for the same drift.
      icon: TARGET_STATUS_PRESENTATION[key]?.icon,
      color: TARGET_STATUS_PRESENTATION[key]?.color,
      hasIntroductionDate: !resets.has('introductionDate'),
      hasMasteryDate: meta.stampsDate === 'masteryDate',
      ...whenTrue(meta.stampsDate === 'holdDate', 'hasHoldDate'),
      ...whenTrue(meta.stampsDate === 'abortedDate', 'hasAbortedDate'),
      ...whenTrue(resets.has('introductionDate'), 'resetIntroductionDate'),
      ...whenTrue(resets.has('masteryDate'), 'resetMasteryDate'),
      ...whenTrue(resets.has('holdDate'), 'resetHoldDate'),
      ...whenTrue(resets.has('abortedDate'), 'resetAbortedDate'),
      canCollectData: meta.canCollectData,
      order: meta.order,
    };
    return [key, Object.defineProperty(entry, 'label', {
      get: () => i18n.t(meta.labelKey),
      enumerable: true,
    })];
  }),
);

/** @todo move to `src/utils/ProgramUtils.js` */
const programTypes = {
  simple: {
    value: 'simple',
    label: 'SkillAcquisition',
    group: 'data',
    hideFromProgress: false,
    defaultMetric: 'percentCorrect',
  },
  frequency: {
    value: 'frequency',
    label: 'Frequency',
    group: 'data',
    hideFromProgress: false,
    targetDataType: 'discreteTrial',
    defaultMetric: 'frequency',
    limitMetricGroups: ['percentCorrect', 'frequency'],
  },
  duration: {
    value: 'duration',
    label: 'Duration',
    group: 'data',
    hideFromProgress: false,
    targetDataType: 'duration',
    defaultMetric: 'totalDuration',
    limitMetricGroups: ['percentCorrect', 'frequency', 'duration'],
  },
  interval: {
    value: 'interval',
    label: 'Interval',
    group: 'data',
    hideFromProgress: false,
    defaultMetric: 'percentCorrect',
    limitMetricGroups: ['percentCorrect', 'frequency'],
  },
  behaviour: { value: 'behaviour', label: 'Behavior', group: 'behavior', hideFromProgress: true },
  taskAnalysis: {
    value: 'taskAnalysis',
    label: 'TaskAnalysis',
    group: 'data',
    hideFromProgress: false,
    defaultMetric: 'percentCorrect',
  },
  esdm: { value: 'esdm', label: 'ESDM', group: 'data', hideFromProgress: false, defaultMetric: 'percentCorrect' },
  instructions: { value: 'instructions', label: 'Instructions', group: 'data', hideFromProgress: true },
  sbt: {
    value: 'sbt',
    label: 'SBT',
    group: 'data',
    hideFromProgress: false,
    hasModes: true,
    modes: {
      easy: {
        label: i18n.t('run.collectData.sbtModes.easy'),
        color: 'bg-green-2',
      },
      normal: {
        label: i18n.t('run.collectData.sbtModes.normal'),
        color: 'bg-blue-2',

      },
      challenging: {
        label: i18n.t('run.collectData.sbtModes.challenging'),
        color: 'bg-yellow-2',
      },
    },
    defaultMetric: 'percentCorrect',
  },
  set: {
    value: 'set',
    label: 'SET',
    group: 'data',
    hideFromProgress: false,
    hasModes: true,
    modes: {
      probe: {
        label: i18n.t('run.collectData.setModes.probe'),
        color: 'bg-yellow-2',
      },
      acquisition: {
        label: i18n.t('run.collectData.setModes.acquisition'),
        color: 'bg-red-2',

      },
      mastered: {
        label: i18n.t('run.collectData.setModes.mastered'),
        color: 'bg-green-2',
      },
    },
    defaultMetric: 'percentCorrect',
  },
  ptp: {
    value: 'ptp',
    label: 'ProbeTeachProbe',
    group: 'data',
    hideFromProgress: false,
    hasModes: true,
    modes: {
      preTest: {
        label: i18n.t('run.collectData.probeTeachProbeModes.preTest'),
        color: 'bg-yellow-2',
      },
      teach: {
        label: i18n.t('run.collectData.probeTeachProbeModes.teach'),
        color: 'bg-blue-2',
      },
      postTest: {
        label: i18n.t('run.collectData.probeTeachProbeModes.postTest'),
        color: 'bg-green-2',
      },
    },
    defaultMetric: 'percentCorrect',
    requiresAdmin: true,
  },
};

/**
 * Legacy shape for the files still reading `programStatuses` directly.
 *
 * `label` STAYS AN i18n KEY here, unlike `targetStatuses` above, because that is what this map has
 * always held and its readers pass it to `$t()` themselves. Resolving it would double-translate.
 * The two vocabularies disagreeing about what `label` means is a legacy wart the shim preserves
 * rather than a decision.
 */
const PROGRAM_STATUS_ICONS = {
  waiting: 'pending_actions',
  baseline: 'legend_toggle',
  active: 'directions_run',
  maintenance: 'repeat',
  hold: 'pan_tool',
  closed: 'done_all',
  discontinued: 'block',
};

const programStatuses = Object.fromEntries(
  Object.entries(PROGRAM_STATUS_META).map(([key, meta]) => [key, {
    value: meta.value,
    label: meta.labelKey,
    icon: PROGRAM_STATUS_ICONS[key],
  }]),
);

/** @todo move to utils */
const chartThemeList = [
  {
    value: 'default',
    label: i18n.t('insights.editChartSettings.selectThemeOptions.default'),
  },
  {
    value: 'bw',
    label: i18n.t('insights.editChartSettings.selectThemeOptions.bw'),
  },
];

/** @todo move to `src/utils/TargetUtils.js` */
const defaultTargetGraphSettings = {
  simple: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spSuccessRate',
    xAxis: 'xaDateDaily',
  },
  frequency: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spSuccess',
    xAxis: 'xaDateDaily',
  },
  frequencyObservations: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spOccurrences',
    xAxis: 'xaDateDaily',
  },
  totalDuration: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spTotalDuration',
    xAxis: 'xaDateDaily',
  },
  minDuration: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spMinDuration',
    xAxis: 'xaDateDaily',
  },
  maxDuration: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spMaxDuration',
    xAxis: 'xaDateDaily',
  },
  avgDuration: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spAvgDuration',
    xAxis: 'xaDateDaily',
  },

  totalTask: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spSuccessRate',
    xAxis: 'xaDateDaily',
  },
  successPerTargetStep: {
    dataSource: 'dsSessionData',
    dataSeriesPreset: 'spSuccessRate',
    xAxis: 'xaTarget',
    chartType: 'column',
    xAxisOrderBy: 'targetOrder',
  },
};

/** @todo move to `src/utils/ProgramUtils.js` */
const intervalWarningSounds = [
  { value: 'buzzer', label: 'Buzzer', soundFile: 'interval-alert.mp3' },
  { value: 'bell', label: 'Bell', soundFile: 'bell.mp3' },
  { value: 'bell-tripple', label: 'Bell Triple Ring', soundFile: 'bell-tripple.mp3' },
  { value: 'crystal', label: 'Crystal', soundFile: 'crystal.mp3' },
  { value: 'cymbal', label: 'Cymbal', soundFile: 'cymbal.mp3' },
  { value: 'twinkle', label: 'Twinkle', soundFile: 'twinkle.mp3' },
];

/** @todo move to `src/utils/ProgramUtils.js` */
const programSettings = {
  simple: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
    },
    dataRecording: {
      workflow: true,
      promptLevels: true,
      graphSettings: true,
    },
  },
  frequency: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
    },
    dataRecording: {
      workflow: true,
      promptLevels: true,
      timerAutoStartOption: true,
      graphSettings: true,
    },
  },
  duration: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: false,
      programTargets: true,
    },
    dataRecording: {
      promptLevels: true,
      workflow: true,
      recordZeroDuration: true,
      durationPromptLevelTiming: true,
      graphSettings: true,
    },
  },
  interval: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: false,
      programTargets: true,
    },
    dataRecording: {
      promptLevels: true,
      timerAutoStartOption: true,
      defaultPromptLevel: true,
      defaultPromptLevelClearable: true,
      intervalTime: true,
      timerWarnOption: true,
      workflow: true,
      graphSettings: true,
    },
  },
  esdm: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
      programTargetGoals: true,
    },
    dataRecording: {
      promptLevels: true,
      dependantPromptLevel: true,
      timerAutoStartOption: true,
      intervalTime: true,
      timerWarnOption: true,
      workflow: true,
      graphSettings: true,
    },
  },
  sbt: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      objective: true,
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
      status: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
    },
    dataRecording: {
      workflow: true,
      promptLevels: true,

      defaultPromptLevel: true,
      defaultPromptLevelClearable: true,
      srTarget: true,
      sbtAutoProgression: true,

      durationHeading: true,
      recordZeroDuration: true,
      durationPromptLevelTiming: true,
      graphSettings: true,
    },
  },
  set: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      objective: true,
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
      status: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
    },
    dataRecording: {
      workflow: false,
      promptLevels: true,
      graphSettings: true,
    },
  },
  ptp: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      objective: true,
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
      status: true,
    },
    targets: {
      discriminativeStimulus: true,
      programTargets: true,
    },
    dataRecording: {
      workflow: true,
      promptLevels: true,
      alternativePromptLevelTemplate: true,
      graphSettings: true,
      ptpTeachPhaseDuration: true,
      ptpNumTestTrials: true,
    },
  },
  taskAnalysis: {
    treatment: {
      treatmentAreas: true,
      baseline: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    targets: {
      discriminativeStimulus: false,
      programTargets: true,
    },
    dataRecording: {
      promptLevels: true,
      defaultPromptLevel: true,
      defaultPromptLevelClearable: true,
      workflow: true,
      graphSettings: true,
    },
  },
  instructions: {
    treatment: {
      treatmentAreas: true,
      objective: true,
      status: true,
    },
    content: {
      instructions: true,
      therapistInstructions: true,
      video: true,
      learningMaterials: true,
      tags: true,
    },
    automation: {
      actions: true,
    },
  },
  asr: {
    treatment: {
      status: true,
    },
    content: {
      instructions: true,
      therapistInstructions: true,
      tags: true,
    },
    dataRecording: {
      promptLevels: true,
      taskList: true,
    },
    form: {
      formType: true,
      formQuestions: true,
    },
    automation: {
      actions: true,
    },
  },
  behaviour: {
    treatment: {
      objective: true,
    },
    content: {
      tags: true,
    },
    targets: {
      programBehaviourTargets: true,
    },
  },
};

/** @todo move to `src/utils/TargetUtils.js` */
const targetDataTypes = [
  {
    value: 'discreteTrial',
    label: i18n.t('programs.targets.dataTypes.discreteTrial'),
    showType: false,
    hasSettings: false,
    hasSubTargets: false,
    allowAsSubTarget: true,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency'],
    passThroughMetrics: ['percentCorrect', 'frequency'],
    defaultMetric: 'percentCorrect',
  },
  {
    value: 'duration',
    label: i18n.t('programs.targets.dataTypes.duration'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    allowAsSubTarget: true,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency', 'totalDuration', 'minDuration', 'maxDuration', 'avgDuration'],
    passThroughMetrics: ['totalDuration', 'minDuration', 'maxDuration', 'avgDuration'],
    defaultMetric: 'totalDuration',
  },
  {
    value: 'rate',
    label: i18n.t('programs.targets.dataTypes.rate'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency', 'ratePerHour', 'ratePerMinute'],
    passThroughMetrics: ['ratePerHour', 'ratePerMinute'],
    defaultMetric: 'ratePerHour',
  },
  {
    value: 'taskAnalysis',
    label: i18n.t('programs.targets.dataTypes.taskAnalysis'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: true,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSteps'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSteps'),
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: false,

    hasDefaultObservation: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: true,
    allowedProgramTypes: ['simple', 'sbt', 'asr'],
  },
  {
    value: 'targetSet',
    label: i18n.t('programs.targets.dataTypes.targetSet'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMastering: true,
    hasSubTargetMaintenanceInterval: true,
    preventWorkflowInheritance: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'ptp', 'asr'],
  },
  {
    value: 'shaping',
    label: i18n.t('programs.targets.dataTypes.shaping'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: true,
    hasSubTargetMaintenanceInterval: false,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'asr'],
  },
  {
    value: 'randomRotation',
    label: i18n.t('programs.targets.dataTypes.randomRotation'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: true,
    showMultipleSubTargets: false,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: true,
    hasSubTargetMaintenanceInterval: true,
    limitPrograms: '',
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['sbt'],
  },
  {
    value: 'cabBranch',
    label: i18n.t('programs.targets.dataTypes.cabBranch'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditBranchTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewBranchTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['sbt'],
  },
  {
    value: 'instructions',
    label: i18n.t('programs.targets.dataTypes.instructions'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasPromptLevelTemplate: false,
    hasInstructions: true,
    hasSubTargetMaintenanceInterval: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'sbt', 'ptp', 'asr'],
  },
  {
    value: 'interval',
    label: i18n.t('programs.targets.dataTypes.interval'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    allowAsSubTarget: true,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: true,
    hasSubTargetMastering: false,
    hasPromptLevelTemplate: true,
    hasDefaultObservation: true,
    hasIntervalSettings: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple'],
    allowedMetrics: ['percentCorrect', 'frequency'],
    passThroughMetrics: ['percentCorrect', 'frequency'],
    defaultMetric: 'percentCorrect',
  },
  {
    value: 'frequency',
    label: i18n.t('programs.targets.dataTypes.frequency'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple'],
    allowedMetrics: ['frequency'],
    passThroughMetrics: ['frequency'],
    defaultMetric: 'frequency',
  },
];

const workflowGroups = [
  {
    group: 'accuracy',
    dataType: 'discreteTrial',
    label: i18n.t('settings.workflows.workflowGroups.accuracy'),
    hasMultipleTypes: true,
    workflowTypes: ['accuracyIncrease', 'accuracyReduction'],
  },
  {
    group: 'frequency',
    dataType: 'frequency',
    label: i18n.t('settings.workflows.workflowGroups.frequency'),
    hasMultipleTypes: true,
    workflowTypes: ['frequencyIncrease', 'frequencyReduction'],
  },
  {
    group: 'rate',
    label: i18n.t('settings.workflows.workflowGroups.rate'),
    hasMultipleTypes: true,
    workflowTypes: [
      'ratePerHourIncrease',
      'ratePerHourReduction',
      'ratePerMinuteIncrease',
      'ratePerMinuteReduction',
    ],
  },
  {
    group: 'duration',
    dataType: 'duration',
    label: i18n.t('settings.workflows.workflowGroups.duration'),
    hasMultipleTypes: true,
    workflowTypes: [
      'totalDurationIncrease',
      'totalDurationReduction',
      'minDurationIncrease',
      'minDurationReduction',
      'maxDurationIncrease',
      'maxDurationReduction',
      'avgDurationIncrease',
      'avgDurationReduction',
    ],
  },
  {
    group: 'manual',
    label: i18n.t('settings.workflows.workflowGroups.manual'),
    hasMultipleTypes: false,
    workflowType: 'manual',
  },
];

const WORKFLOW_INTERVALS = Object.freeze({
  DAILY: 1,
  WEEKLY: 7,
  BIWEEKLY: 14,
  MONTHLY: 30,
  QUARTERLY: 91,
  SEMI_ANNUALLY: 183,
  ANNUALLY: 365,
});

const weeklyFourWeeksSchedule = {
  interval: WORKFLOW_INTERVALS.WEEKLY,
  sessions: 4,
};

const workflowTypes = {
  accuracyIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.accuracyIncrease'),
    metric: 'percentCorrect',
    workflowProp: 'accuracy',
    direction: 'increase',
    hasMinNumberOfTrials: true,
    hasColdProbeOption: true,
    sortOrder: 1,
    defaultWorkflowDetails: {
      probe: {
        minTrials: 3,
        accuracy: 100,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        limitTrials: null,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minTrials: 1,
        accuracy: 80,
        sessions: 3,
        limitTrials: null,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        accuracy: 100,
        sessions: 1,
        minTrials: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  }, // accuracyIncrease
  accuracyReduction: {
    label: i18n.t('settings.workflows.workflowTypes.accuracyReduction'),
    metric: 'percentCorrect',
    workflowProp: 'accuracy',
    direction: 'reduction',
    hasMinNumberOfTrials: true,
    hasColdProbeOption: true,
    sortOrder: 2,
    defaultWorkflowDetails: {
      probe: {
        minTrials: 3,
        accuracy: 0,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        limitTrials: null,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minTrials: 1,
        accuracy: null,
        sessions: 3,
        limitTrials: null,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        accuracy: 0,
        sessions: 1,
        minTrials: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  }, // accuracyReduction
  frequencyIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.frequencyIncrease'),
    metric: 'frequency',
    workflowProp: 'minFrequency',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 3,
    defaultWorkflowDetails: {
      probe: {
        minFrequency: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minFrequency: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minFrequency: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },

  },
  frequencyReduction: {
    label: i18n.t('settings.workflows.workflowTypes.frequencyReduction'),
    metric: 'frequency',
    workflowProp: 'maxFrequency',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 4,
    defaultWorkflowDetails: {
      probe: {
        maxFrequency: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxFrequency: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxFrequency: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  ratePerHourIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.ratePerHourIncrease'),
    metric: 'ratePerHour',
    workflowProp: 'minRatePerHour',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 5,
    defaultWorkflowDetails: {
      probe: {
        minRatePerHour: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minRatePerHour: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minRatePerHour: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  ratePerHourReduction: {
    label: i18n.t('settings.workflows.workflowTypes.ratePerHourReduction'),
    metric: 'ratePerHour',
    workflowProp: 'maxRatePerHour',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 6,
    defaultWorkflowDetails: {
      probe: {
        maxRatePerHour: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxRatePerHour: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxRatePerHour: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  ratePerMinuteIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.ratePerMinuteIncrease'),
    metric: 'ratePerMinute',
    workflowProp: 'minRatePerMinute',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 7,
    defaultWorkflowDetails: {
      probe: {
        minRatePerMinute: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minRatePerMinute: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minRatePerMinute: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  ratePerMinuteReduction: {
    label: i18n.t('settings.workflows.workflowTypes.ratePerMinuteReduction'),
    metric: 'ratePerMinute',
    workflowProp: 'maxRatePerMinute',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 8,
    defaultWorkflowDetails: {
      probe: {
        maxRatePerMinute: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxRatePerMinute: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxRatePerMinute: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  totalDurationIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.totalDurationIncrease'),
    metric: 'totalDuration',
    workflowProp: 'minTotalDuration',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 9,
    defaultWorkflowDetails: {
      probe: {
        minTotalDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minTotalDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minTotalDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  totalDurationReduction: {
    label: i18n.t('settings.workflows.workflowTypes.totalDurationReduction'),
    metric: 'totalDuration',
    workflowProp: 'maxTotalDuration',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 10,
    defaultWorkflowDetails: {
      probe: {
        maxTotalDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxTotalDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxTotalDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  minDurationIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.minDurationIncrease'),
    metric: 'minDuration',
    workflowProp: 'minMinDuration',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 11,
    defaultWorkflowDetails: {
      probe: {
        minMinDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minMinDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minMinDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  minDurationReduction: {
    label: i18n.t('settings.workflows.workflowTypes.minDurationReduction'),
    metric: 'minDuration',
    workflowProp: 'maxMinDuration',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 12,
    defaultWorkflowDetails: {
      probe: {
        maxMinDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxMinDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxMinDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  maxDurationIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.maxDurationIncrease'),
    metric: 'maxDuration',
    workflowProp: 'minMaxDuration',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 13,
    defaultWorkflowDetails: {
      probe: {
        minMaxDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minMaxDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minMaxDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  maxDurationReduction: {
    label: i18n.t('settings.workflows.workflowTypes.maxDurationReduction'),
    metric: 'maxDuration',
    workflowProp: 'maxMaxDuration',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 14,
    defaultWorkflowDetails: {
      probe: {
        maxMaxDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxMaxDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxMaxDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  avgDurationIncrease: {
    label: i18n.t('settings.workflows.workflowTypes.avgDurationIncrease'),
    metric: 'avgDuration',
    workflowProp: 'minAvgDuration',
    direction: 'increase',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 15,
    defaultWorkflowDetails: {
      probe: {
        minAvgDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        minAvgDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        minAvgDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  avgDurationReduction: {
    label: i18n.t('settings.workflows.workflowTypes.avgDurationReduction'),
    metric: 'avgDuration',
    workflowProp: 'maxAvgDuration',
    direction: 'reduction',
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 16,
    defaultWorkflowDetails: {
      probe: {
        maxAvgDuration: null,
        sessions: 1,
        masteredStatus: 'closed',
        failedStatus: 'acquisition',
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      acquisition: {
        maxAvgDuration: null,
        sessions: 3,
        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      mastered: {
        maintenanceSchedule: [weeklyFourWeeksSchedule],
        revertStrategy: null,
        maxAvgDuration: null,
        sessions: 1,

        addPhaseLine: false,
        addPhaseLineLabel: null,
      },
      waiting: {
        autoOpenTargets: false,
        autoOpenTargetsCount: 5,
        autoOpenTargetsToStatus: 'probe',
        autoOpenDirection: 'forward',
      },
    },
  },
  manual: {
    label: i18n.t('settings.workflows.workflowTypes.manual'),
    metrics: [],
    showAlways: true,
    hasMinNumberOfTrials: false,
    hasColdProbeOption: false,
    sortOrder: 17,
    defaultWorkflowDetails: {
      probe: { },
      acquisition: { },
      mastered: { },
      waiting: {
        autoOpenTargets: false,
      },
    },
  },
};

const measurementMetricGroups = [
  {
    id: 'percentCorrect',
    label: i18n.t('measurementMetrics.percentCorrect.label'),
    icon: 'percent',
    defaultMeasurementMetric: 'percentCorrect',
    defaultTargetType: 'discreteTrial',
  },
  {
    id: 'frequency',
    label: i18n.t('measurementMetrics.frequency.label'),
    icon: 'bar_chart',
    defaultMeasurementMetric: 'frequency',
    defaultTargetType: 'discreteTrial',
  },
  {
    id: 'rate',
    label: i18n.t('measurementMetrics.rate.label'),
    icon: 'speed',
    defaultMeasurementMetric: 'ratePerHour',
    defaultTargetType: 'rate',
  },
  {
    id: 'duration',
    label: i18n.t('measurementMetrics.duration.label'),
    icon: 'timer',
    defaultMeasurementMetric: 'totalDuration',
    defaultTargetType: 'duration',
  },
];

const measurementMetrics = [
  {
    id: 'percentCorrect',
    measurementMetricGroup: 'percentCorrect',
    label: i18n.t('measurementMetrics.percentCorrect.label'),
    dataSeriesPreset: 'spSuccessRate',
    suffix: '%',
    workflowType: 'discreteTrial',
    order: 1,
    calcFunc: (val) => val / 100,
  },
  {
    id: 'frequency',
    measurementMetricGroup: 'frequency',
    label: i18n.t('measurementMetrics.frequency.label'),
    defaultTargetType: 'discreteTrial',
    dataSeriesPreset: 'spSuccess',
    suffix: i18n.t('measurementMetrics.frequency.unit'),
    workflowType: 'frequency',
    order: 1,
  },
  {
    id: 'ratePerHour',
    measurementMetricGroup: 'rate',
    label: i18n.t('measurementMetrics.ratePerHour.label'),
    suffix: i18n.t('measurementMetrics.ratePerHour.unit'),
    workflowType: 'rate',
    dataSeriesPreset: 'spFrequencySuccessesPerHour',
    order: 1,
    calcFunc: (val) => val,
  },
  {
    id: 'ratePerMinute',
    measurementMetricGroup: 'rate',
    label: i18n.t('measurementMetrics.ratePerMinute.label'),
    suffix: i18n.t('measurementMetrics.ratePerMinute.unit'),
    workflowType: 'rate',
    dataSeriesPreset: 'spFrequencySuccessesPerMinute',
    order: 2,
    calcFunc: (val) => val,
  },
  {
    id: 'totalDuration',
    measurementMetricGroup: 'duration',
    label: i18n.t('measurementMetrics.totalDuration.label'),
    workflowType: 'duration',
    dataSeriesPreset: 'spTotalDuration',
    icon: 'update',
    order: 1,
  },
  {
    id: 'minDuration',
    measurementMetricGroup: 'duration',
    label: i18n.t('measurementMetrics.minDuration.label'),
    workflowType: 'duration',
    dataSeriesPreset: 'spMinDuration',
    icon: 'minimize',
    order: 2,
  },
  {
    id: 'maxDuration',
    measurementMetricGroup: 'duration',
    label: i18n.t('measurementMetrics.maxDuration.label'),
    workflowType: 'duration',
    dataSeriesPreset: 'spMaxDuration',
    icon: 'maximize',
    order: 2,
  },
  {
    id: 'avgDuration',
    measurementMetricGroup: 'duration',
    label: i18n.t('measurementMetrics.avgDuration.label'),
    workflowType: 'duration',
    dataSeriesPreset: 'spAvgDuration',
    icon: 'equalizer',
    order: 2,
  },
];

// "async" is optional
export {
  chartThemeList,
  defaultPromptLevelColors,
  defaultTargetGraphSettings,
  filterPropertyTypes,
  intervalWarningSounds,
  measurementMetricGroups,
  measurementMetrics,
  programSettings,
  programStatuses,
  programTypes,
  targetDataTypes,
  targetStatuses,
  workflowGroups,
  workflowTypes,
  WORKFLOW_INTERVALS,
};
